import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Ban,
  FileArchive,
  ImagePlus,
  Loader2,
  Plus,
  Settings2,
  Sparkles,
  Star,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient, parseApiError } from "@/app/services/api";
import { LANGUAGES, type Lang } from "@/app/i18n/types";
import { toMlObj, pick, stripEmpty, stripEmptyFeatures } from "@/app/utils/mlField";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Textarea } from "@/app/components/ui/textarea";
import { Label, RequiredLabel } from "@/app/components/ui/label";
import { FieldHint } from "@/app/components/ui/field-hint";
import { Badge } from "@/app/components/ui/badge";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { TagInput } from "@/app/components/ui/tag-input";
import { MlField, useMlField } from "@/app/components/ui/ml-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { humanSize, useFormatUiDateTime } from "@/app/utils/format";
import type { AdminAppDetail, AdminScreenshot, AdminVersion } from "@/app/types/api";

type Screenshot = {
  title: Partial<Record<Lang, string>>;
  caption: Partial<Record<Lang, string>>;
  image_key: string;
};

function mediaUrl(appId: string, key: string | null | undefined) {
  if (!key) return "";
  if (/^https?:/i.test(key)) return key;
  const fn = String(key).split("/").pop();
  return `/api/v1/apps/${encodeURIComponent(appId)}/media/${encodeURIComponent(fn ?? "")}`;
}

export function AppOverviewCard({ d }: { d: AdminAppDetail }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "vi") as Lang;
  const latest =
    d.versions.find((version) => version.is_latest) ?? d.versions[0] ?? null;
  const screenshots = d.screenshots ?? [];

  return (
    <Card className="overflow-hidden gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <FileArchive className="h-4 w-4 text-primary" />
          {d.app_id}
        </CardTitle>
        <CardDescription>{pick(d.description_short, lang) || pick(d.description, lang) || d.vendor || "—"}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-5 pb-5 pt-4">
        <div className="rounded-xl border border-border bg-muted/20 p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {d.plan_type === "premium" && (
              <Badge variant="primary">
                <Star className="mr-0.5 h-3 w-3" />
                {t("plan.premium")}
              </Badge>
            )}
            {d.category ? <Badge variant="outline">{d.category}</Badge> : null}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <SummaryCell label={t("field.vendor")} value={d.vendor || "—"} />
            <SummaryCell label={t("detail.versions", { count: d.versions.length })} value={String(d.versions.length)} />
            <SummaryCell label={t("field.screenshots")} value={String(screenshots.length)} />
            <SummaryCell label={t("field.tags")} value={String((d.tags ?? []).length)} />
            <SummaryCell label={t("detail.downloads", { count: d.download_count })} value={String(d.download_count ?? 0)} />
            <SummaryCell
              label={t("detail.rating", { rating: d.rating })}
              value={d.rating ? String(d.rating) : "—"}
            />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-background/70 p-4">
          <Label className="mb-2 text-xs font-medium text-muted-foreground">
            {latest ? `${t("version.row.actions")} · v${latest.version}` : t("version.noVersions")}
          </Label>
          {latest ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <SummaryCell
                label={t("version.row.platform")}
                value={latest.platform || "—"}
              />
              <SummaryCell
                label={t("version.row.python")}
                value={latest.python_version || "—"}
              />
              <SummaryCell
                label={t("version.row.size")}
                value={humanSize(latest.size_bytes)}
              />
              <SummaryCell
                label={t("version.row.minCore")}
                value={latest.min_core_version || "—"}
              />
            </div>
          ) : (
            <FieldHint>{t("version.noVersions")}</FieldHint>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CurateSection({
  d,
  onSaved,
}: {
  d: AdminAppDetail;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "vi") as Lang;
  const [vendor, setVendor] = React.useState(d.vendor ?? "");
  const [name, setName] = useMlField(toMlObj(d.name) as Partial<Record<Lang, string>>);
  const [descShort, setDescShort] = useMlField(
    toMlObj(d.description_short) as Partial<Record<Lang, string>>
  );
  const [desc, setDesc] = useMlField(
    toMlObj(d.description) as Partial<Record<Lang, string>>
  );
  const [features, setFeatures] = useMlField(
    toMlObj(d.features) as Partial<Record<Lang, string>>
  );
  const [changelog, setChangelog] = useMlField(
    toMlObj((d.versions.find((v) => v.is_latest) ?? d.versions[0])?.changelog) as Partial<
      Record<Lang, string>
    >
  );
  const [depsText, setDepsText] = React.useState(
    ((d.versions.find((v) => v.is_latest) ?? d.versions[0])?.dependencies ?? []).join("\n")
  );
  const [tags, setTags] = React.useState<string[]>(d.tags ?? []);
  const [screenshots, setScreenshots] = React.useState<Screenshot[]>(
    (d.screenshots ?? []).map((s: AdminScreenshot) => ({
      title: toMlObj(s.title) as Partial<Record<Lang, string>>,
      caption: toMlObj(s.caption) as Partial<Record<Lang, string>>,
      image_key: s.image_key ?? "",
    }))
  );

  const fileRef = React.useRef<HTMLInputElement>(null);

  const uploadShot = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiClient.post<{ image_key: string }>(
        `/api/v1/admin/apps/${encodeURIComponent(d.app_id)}/media`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return { res: res.data, file };
    },
    onSuccess: ({ res, file }) => {
      setScreenshots((prev) => [
        ...prev,
        {
          title: { [lang]: file.name.replace(/\.[^.]+$/, "") },
          caption: {},
          image_key: res.image_key,
        },
      ]);
      toast.success(t("toast.uploadedShot"));
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        vendor: vendor || undefined,
        name: stripEmpty(name),
        description_short: stripEmpty(descShort),
        description: stripEmpty(desc),
        changelog: stripEmpty(changelog),
        features: stripEmptyFeatures(features),
        dependencies: depsText.split("\n").map((s) => s.trim()).filter(Boolean),
        tags: tags.slice(),
        screenshots: screenshots.map((s) => ({
          title: s.title,
          caption: s.caption,
          image_key: s.image_key || null,
        })),
      };
      await apiClient.patch(`/api/v1/admin/apps/${encodeURIComponent(d.app_id)}`, body);
    },
    onSuccess: () => {
      toast.success(t("toast.savedApp", { app: d.app_id }));
      onSaved();
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  return (
    <Card className="flex-1 gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          {t("detail.displayInfo")}
        </CardTitle>
        <CardDescription>{t("field.description")}</CardDescription>
        <CardAction className="flex items-center gap-2">
          <Badge variant="outline">{lang.toUpperCase()}</Badge>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("detail.save")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4 px-5 pb-5 pt-4">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,4fr)]">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("field.vendor")}</Label>
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </div>
          <MlField
            base="e_name"
            lang={lang}
            label={t("detail.displayName")}
            className="flex flex-col gap-1.5"
            inputClassName="h-9"
            value={name}
            onChange={setName}
          />
          <MlField
            base="e_desc_short"
            lang={lang}
            label={t("field.descShort")}
            className="flex flex-col gap-1.5"
            inputClassName="h-9"
            value={descShort}
            onChange={setDescShort}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <MlField
            base="e_desc"
            lang={lang}
            label={t("field.description")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={desc}
            onChange={setDesc}
          />
          <MlField
            base="e_features"
            lang={lang}
            label={t("detail.features")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={features}
            onChange={setFeatures}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("field.tags")}</Label>
            <TagInput value={tags} onChange={setTags} placeholder={t("field.tags.placeholder")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("detail.deps")}</Label>
            <Textarea
              value={depsText}
              onChange={(e) => setDepsText(e.target.value)}
              rows={5}
              className="min-h-[96px] resize-y"
            />
          </div>
          <MlField
            base="e_changelog"
            lang={lang}
            label={t("detail.changelogLatest")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={changelog}
            onChange={setChangelog}
          />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50/90 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-amber-900">{t("field.screenshots")}</div>
              <div className="mt-1">
                <FieldHint className="text-amber-800/90">{t("detail.shotHint")}</FieldHint>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="soft"
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadShot.isPending}
              >
                {uploadShot.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ImagePlus className="h-3.5 w-3.5" />
                )}
                {t("detail.addShot")}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadShot.mutate(f);
                  e.target.value = "";
                }}
              />
            </div>
          </div>
          <div className="mt-4">
            <ScreenshotList
              appId={d.app_id}
              screenshots={screenshots}
              lang={lang}
              onChange={setScreenshots}
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between gap-3 border-t border-border px-5 pb-5 pt-4">
        <div className="min-w-0 text-xs text-muted-foreground">{d.app_id}</div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("detail.save")}
        </Button>
      </CardFooter>
    </Card>
  );
}

function ScreenshotList({
  appId,
  screenshots,
  lang,
  onChange,
}: {
  appId: string;
  screenshots: Screenshot[];
  lang: Lang;
  onChange: (next: Screenshot[]) => void;
}) {
  const { t } = useTranslation();
  if (!screenshots.length) {
    return (
      <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">
        {t("shots.empty")}
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {screenshots.map((s, i) => {
        const url = mediaUrl(appId, s.image_key);
        return (
          <div key={i} className="flex items-start gap-2 rounded-xl border border-border bg-background/70 p-2.5">
            {url ? (
              <img src={url} alt="" className="h-16 w-24 rounded-lg border border-border object-cover" />
            ) : (
              <div className="grid h-16 w-24 place-items-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
                ?
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-1.5">
              <Input
                value={s.title[lang] ?? ""}
                onChange={(e) => {
                  const next = screenshots.slice();
                  next[i] = { ...next[i], title: { ...next[i].title, [lang]: e.target.value } };
                  onChange(next);
                }}
                placeholder={t("shots.titlePlaceholder", { lang: lang.toUpperCase() })}
              />
              <Input
                value={s.caption[lang] ?? ""}
                onChange={(e) => {
                  const next = screenshots.slice();
                  next[i] = { ...next[i], caption: { ...next[i].caption, [lang]: e.target.value } };
                  onChange(next);
                }}
                placeholder={t("shots.captionPlaceholder", { lang: lang.toUpperCase() })}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const next = screenshots.slice();
                next.splice(i, 1);
                onChange(next);
              }}
              className="rounded p-1 hover:bg-accent"
              aria-label="remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function VersionsSection({
  d,
  onChanged,
  onAddVersion,
}: {
  d: AdminAppDetail;
  onChanged: () => void;
  onAddVersion: () => void;
}) {
  const { t } = useTranslation();
  const [yankVer, setYankVer] = React.useState<string | null>(null);
  const [deprecateVer, setDeprecateVer] = React.useState<string | null>(null);
  return (
    <Card className="gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-primary" />
          {t("detail.versions", { count: d.versions.length })}
        </CardTitle>
        <CardDescription>{d.app_id}</CardDescription>
        <CardAction>
          <Button size="sm" onClick={onAddVersion}>
            <Plus className="h-3.5 w-3.5" />
            {t("detail.addVersion")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-3 px-5 pb-5 pt-4">
        {d.versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("version.noVersions")}</p>
        ) : (
          <VersionList d={d} onChanged={onChanged} onYank={setYankVer} onDeprecate={setDeprecateVer} />
        )}
      </CardContent>
      <YankDialog
        ver={yankVer}
        onClose={() => setYankVer(null)}
        onSubmit={async (reason) => {
          await apiClient.post(
            `/api/v1/admin/apps/${encodeURIComponent(d.app_id)}/versions/${encodeURIComponent(yankVer!)}/yank`,
            { reason: reason || null }
          );
        }}
      />
      <DeprecateDialog
        ver={deprecateVer}
        onClose={() => setDeprecateVer(null)}
        onSubmit={async (message, supersededBy) => {
          await apiClient.post(
            `/api/v1/admin/apps/${encodeURIComponent(d.app_id)}/versions/${encodeURIComponent(deprecateVer!)}/deprecate`,
            { message, superseded_by: supersededBy || null }
          );
        }}
      />
    </Card>
  );
}

function VersionList({
  d,
  onChanged,
  onYank,
  onDeprecate,
}: {
  d: AdminAppDetail;
  onChanged: () => void;
  onYank: (v: string) => void;
  onDeprecate: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "vi") as Lang;
  const fmt = useFormatUiDateTime();

  const vAction = useMutation({
    mutationFn: async (input: {
      method: "POST" | "DELETE";
      ver: string;
      path: "/release" | "/yank" | "/deprecate";
      body?: unknown;
    }) => {
      return apiClient.request({
        method: input.method,
        url: `/api/v1/admin/apps/${encodeURIComponent(d.app_id)}/versions/${encodeURIComponent(input.ver)}${input.path}`,
        data: input.body,
      });
    },
    onSuccess: (_, vars) => {
      toast.success(t("toast.updatedVersion", { version: vars.ver }));
      onChanged();
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  const handle = (act: string, ver: string) => {
    if (act === "release") {
      vAction.mutate({ method: "POST", ver, path: "/release" });
    } else if (act === "unyank") {
      vAction.mutate({ method: "DELETE", ver, path: "/yank" });
    } else if (act === "undeprecate") {
      vAction.mutate({ method: "DELETE", ver, path: "/deprecate" });
    } else if (act === "yank") {
      onYank(ver);
    } else if (act === "deprecate") {
      onDeprecate(ver);
    }
  };

  const versions = d.versions;

  return (
    <div className="space-y-3">
      {versions.map((v: AdminVersion) => {
        const notes: string[] = [];
        if (v.is_deprecated && v.deprecation_message) {
          notes.push(`${v.deprecation_message}${v.superseded_by ? ` (→ v${v.superseded_by})` : ""}`);
        }
        if (v.is_yanked && v.yanked_reason) {
          notes.push(v.yanked_reason);
        }

        return (
          <div key={v.version} className="rounded-xl border border-border bg-muted/15 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-semibold text-foreground">v{v.version}</div>
                  {v.is_latest && <Star className="h-3.5 w-3.5 fill-current text-primary" />}
                  <VersionBadges v={v} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t("version.row.published")}: {v.published_at ? fmt(v.published_at) : "—"}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {v.lifecycle === "draft" ? (
                  <Button size="sm" variant="soft" onClick={() => handle("release", v.version)}>
                    {t("version.action.release")}
                  </Button>
                ) : (
                  <>
                    {v.is_yanked ? (
                      <Button size="sm" variant="outline" onClick={() => handle("unyank", v.version)}>
                        {t("version.action.unyank")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handle("yank", v.version)}>
                        {t("version.action.yank")}
                      </Button>
                    )}
                    {v.is_deprecated ? (
                      <Button size="sm" variant="outline" onClick={() => handle("undeprecate", v.version)}>
                        {t("version.action.undeprecate")}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handle("deprecate", v.version)}>
                        {t("version.action.deprecate")}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
              <SummaryCell label={t("version.row.platform")} value={v.platform || "—"} />
              <SummaryCell label={t("version.row.python")} value={v.python_version || "—"} />
              <SummaryCell label={t("version.row.size")} value={humanSize(v.size_bytes)} />
              <SummaryCell label={t("version.row.minCore")} value={v.min_core_version || "—"} />
            </div>

            <div className="mt-3 grid gap-2 xl:grid-cols-2">
              <InfoPanel
                label={t("version.row.dependencies")}
                value={v.dependencies?.join(", ") || "—"}
              />
              <InfoPanel
                label={t("version.row.note")}
                value={notes.length ? notes.join(" · ") : "—"}
              />
            </div>

            <div className="mt-3 rounded-lg border border-border/70 bg-background/70 px-3 py-2">
              <div className="text-[11px] uppercase tracking-[0.02em] text-muted-foreground">
                {t("version.row.changelog")}
              </div>
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {pick(v.changelog, lang) || "—"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VersionBadges({ v }: { v: AdminVersion }) {
  const { t } = useTranslation();

  return (
    <>
      {v.lifecycle === "draft" ? (
        <Badge variant="warning">{t("state.draft")}</Badge>
      ) : v.is_yanked ? (
        <Badge variant="danger">
          <Ban className="mr-0.5 h-3 w-3" />
          {t("version.lifecycle.yanked")}
        </Badge>
      ) : (
        <Badge variant="success">{t("version.lifecycle.released")}</Badge>
      )}
      {v.is_deprecated ? (
        <Badge variant="warning">
          <AlertTriangle className="mr-0.5 h-3 w-3" />
          {t("version.lifecycle.deprecated")}
        </Badge>
      ) : null}
    </>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background/70 px-3 py-2">
      <div className="text-[11px] uppercase tracking-[0.02em] text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

function InfoPanel({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background/70 px-3 py-2">
      <div className="text-[11px] uppercase tracking-[0.02em] text-muted-foreground">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">{value}</div>
    </div>
  );
}

void LANGUAGES;

function YankDialog({
  ver,
  onClose,
  onSubmit,
}: {
  ver: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const submit = useMutation({
    mutationFn: () => onSubmit(reason.trim()),
    onSuccess: () => {
      toast.success(t("toast.updatedVersion", { version: ver! }));
      onClose();
    },
    onError: (e) => toast.error(parseApiError(e)),
    onSettled: () => setPending(false),
  });
  React.useEffect(() => {
    if (ver !== null) {
      setReason("");
    }
  }, [ver]);
  return (
    <Dialog open={ver !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dialog.yank.title", { ver: ver ?? "" })}</DialogTitle>
          <DialogDescription>{t("dialog.yank.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="yank-reason">{t("dialog.yank.reason")}</Label>
            <Textarea id="yank-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t("dialog.common.cancel")}
          </Button>
          <Button
            variant="destructive"
            disabled={pending}
            onClick={() => {
              setPending(true);
              submit.mutate();
            }}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("dialog.yank.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeprecateDialog({
  ver,
  onClose,
  onSubmit,
}: {
  ver: string | null;
  onClose: () => void;
  onSubmit: (message: string, supersededBy: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [message, setMessage] = React.useState("");
  const [supersededBy, setSupersededBy] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submit = useMutation({
    mutationFn: () => onSubmit(message.trim(), supersededBy.trim()),
    onSuccess: () => {
      toast.success(t("toast.updatedVersion", { version: ver! }));
      onClose();
    },
    onError: (e) => toast.error(parseApiError(e)),
    onSettled: () => setPending(false),
  });
  React.useEffect(() => {
    if (ver !== null) {
      setMessage("");
      setSupersededBy("");
      setError(null);
    }
  }, [ver]);
  const onConfirm = () => {
    if (!message.trim()) {
      setError(t("toast.needDeprecation"));
      return;
    }
    setError(null);
    setPending(true);
    submit.mutate();
  };
  return (
    <Dialog open={ver !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dialog.deprecate.title", { ver: ver ?? "" })}</DialogTitle>
          <DialogDescription>{t("dialog.deprecate.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <RequiredLabel htmlFor="deprecate-msg">{t("dialog.deprecate.message")}</RequiredLabel>
            <Textarea
              id="deprecate-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              aria-invalid={!!error}
              required
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="deprecate-sup">{t("dialog.deprecate.supersededBy")}</Label>
            <Input
              id="deprecate-sup"
              value={supersededBy}
              onChange={(e) => setSupersededBy(e.target.value)}
              placeholder="vd: 1.2.0"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t("dialog.common.cancel")}
          </Button>
          <Button variant="destructive" disabled={pending} onClick={onConfirm}>
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("dialog.deprecate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
